import React, { useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress, Divider } from '@mui/material';
import { DeleteConfirmationModal } from '@/components/shared/DeleteConfirmationModal';
import {
  GetInspectionPlansQuery,
  useGetInspectionPlansQuery,
  useUpdateInspectionPlanMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAtom, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { PlanPill } from '@/components/shared/PlanPill';
import { abyssColors } from '@/theme/colors';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import { applyInspectionPlanFilters } from '@/utils';
import { useInspectionPlanDelete } from '@/components/Analysis/Insights/hooks/useInspectionPlanDelete';

type InspectionPlan = NonNullable<GetInspectionPlansQuery['getInspectionPlans']>[number];

const DEFAULT_PLAN_ID = '__default_example_platform__';

const InspectionPlansPills = () => {
  const [plans, setPlans] = useAtom(state.inspectionPlansState);
  const [selectedPlanId, setSelectedPlanId] = useAtom(state.selectedInspectionPlanId);
  const setSelectedTemplateFilters = useSetAtom(state.selectedTemplateFiltersState);
  const setSelectedDeckIds = useSetAtom(state.selectedFilteredDeckIds);
  const setSelectedWorkpackIds = useSetAtom(state.selectedFilteredWorkpackIds);
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);
  const setApplyFilters = useSetAtom(state.applyFilters);
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS);
  const [pinningPlanId, setPinningPlanId] = useState<string | null>(null);

  const structureId = useGetStructureId();
  const { data, loading, error } = useGetInspectionPlansQuery({
    variables: { structureId },
  });

  const [updateInspectionPlan] = useUpdateInspectionPlanMutation();
  const { showSnackBar } = useSnackBarMessage({});

  const defaultPlan = useMemo<InspectionPlan>(
    () =>
      ({
        id: DEFAULT_PLAN_ID,
        name: 'Example platform',
        // no filters
        filters: {
          deckIds: [],
          workpackIds: [],
          paintRegionIds: [],
          templateFilters: [],
        },
        isPlanPinned: false,
      }) as unknown as InspectionPlan,
    []
  );

  useEffect(() => {
    const fetchedPlans = data?.getInspectionPlans;
    if (!fetchedPlans) return;

    setPlans((prev) => {
      const prevById = new Map(prev.map((p) => [p.id, p] as const));
      const fetchedIds = new Set(fetchedPlans.map((p) => p.id));
      const merged = fetchedPlans.map((p) => {
        const previous = prevById.get(p.id);
        if (!previous) return p;
        return {
          ...p,
          isPlanPinned: previous.isPlanPinned ?? p.isPlanPinned,
        };
      });

      const extras = prev.filter((p) => !fetchedIds.has(p.id));
      return [...merged, ...extras];
    });

    // Do not auto-select a plan; selection happens only via user interaction
  }, [data, setPlans, setSelectedPlanId]);

  const { deleteState, handleDelete, confirmDelete, cancelDelete } = useInspectionPlanDelete();

  if (loading) return <CircularProgress size={20} />;
  if (error) return null;

  const pinnedPlans = plans.filter((plan) => plan.isPlanPinned);
  const selectedUnpinnedPlan =
    selectedPlanId && !pinnedPlans.some((p) => p.id === selectedPlanId)
      ? plans.find((p) => p.id === selectedPlanId)
      : null;

  const clearActivePlanFilters = () => {
    setSelectedTemplateFilters([]);
    setSelectedDeckIds([]);
    setSelectedWorkpackIds([]);
    setSelectedPaintRegionIds([]);
    setSelectedPlanId('');
    setApplyFilters(true);
  };

  const applyDefaultPlanFilters = () => {
    setSelectedTemplateFilters([]);
    setSelectedDeckIds([]);
    setSelectedWorkpackIds([]);
    setSelectedPaintRegionIds([]);
    setSelectedPlanId(DEFAULT_PLAN_ID);
    setApplyFilters(true);
  };

  const applyPlanFilters = (planId: string) => {
    const selectedPlan = plans.find((p) => p.id === planId);
    if (!selectedPlan) return;

    const { templateFilters, deckIds, workpackIds, paintRegionIds, planId: returnedPlanId } =
      applyInspectionPlanFilters(selectedPlan);

    setSelectedTemplateFilters(templateFilters);
    setSelectedDeckIds(deckIds);
    setSelectedWorkpackIds(workpackIds);
    setSelectedPaintRegionIds(paintRegionIds);
    setSelectedPlanId(returnedPlanId);
    setApplyFilters(true);
  };

  const handleTogglePin = async (planId: string, currentlyPinned: boolean) => {
    if (!isAdmin && !canManageInspectionPlans) {
      showSnackBar('user does not have permission to update this view', 'error');
      return;
    }
    try {
      setPinningPlanId(planId);
      await updateInspectionPlan({
        variables: { input: { planId, structureId, isPlanPinned: !currentlyPinned } },
      });
      setPlans((prev) =>
        prev.map((p) => (p.id === planId ? { ...p, isPlanPinned: !currentlyPinned } : p))
      );

      if (currentlyPinned === true && selectedPlanId === planId) {
        clearActivePlanFilters();
      }

      showSnackBar(!currentlyPinned ? 'Plan pinned' : 'Plan unpinned', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      showSnackBar(message, 'error');
    } finally {
      setPinningPlanId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Default plan pill: render first only when there is at least one pinned plan */}
      {pinnedPlans.length > 0 && (
        <>
          <PlanPill
            plan={defaultPlan}
            isSelected={selectedPlanId === DEFAULT_PLAN_ID}
            pinningPlanId={null}
            onClick={applyDefaultPlanFilters}
            onClose={() => {}}
            onPinClick={() => {}}
            showCloseIcon={false}
            showPinIcon={false}
          />
          <Box sx={{ display: 'flex', height: '2rem', alignItems: 'center' }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderRightWidth: '0.2rem', borderColor: abyssColors.primary[600] }}
            />
          </Box>
        </>
      )}

      {pinnedPlans.map((plan, idx) => (
        <React.Fragment key={plan.id}>
          {idx > 0 && (
            <Box sx={{ display: 'flex', height: '2rem', alignItems: 'center' }}>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderRightWidth: '0.2rem', borderColor: abyssColors.primary[600] }}
              />
            </Box>
          )}
          <PlanPill
            plan={plan}
            isSelected={plan.id === selectedPlanId}
            pinningPlanId={pinningPlanId}
            onClick={() => {
              setSelectedPlanId(plan.id);
              applyPlanFilters(plan.id);
            }}
            onClose={() => handleDelete(plan.id)}
            onPinClick={() => handleTogglePin(plan.id, !!plan.isPlanPinned)}
          />
        </React.Fragment>
      ))}

      {selectedUnpinnedPlan && (
        <>
          {pinnedPlans.length > 0 && (
            <Box sx={{ display: 'flex', height: '2rem', alignItems: 'center' }}>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderRightWidth: '0.2rem', borderColor: abyssColors.primary[600] }}
              />
            </Box>
          )}
          <PlanPill
            plan={selectedUnpinnedPlan}
            isSelected={true}
            pinningPlanId={pinningPlanId}
            onClick={() => {
              setSelectedPlanId(selectedUnpinnedPlan.id);
              applyPlanFilters(selectedUnpinnedPlan.id);
            }}
            onClose={() => handleDelete(selectedUnpinnedPlan.id)}
            onPinClick={() => handleTogglePin(selectedUnpinnedPlan.id, !!selectedUnpinnedPlan.isPlanPinned)}
          />
        </>
      )}

      <DeleteConfirmationModal
        title={'Are you sure ?'}
        isOpen={deleteState.isOpen}
        itemName="this plan"
        description="Are you sure you want to delete this plan? This action will permanently remove the plan and its data."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        handleClose={cancelDelete}
        sxStyle={{ bottom: '10% !important' }}
      />
    </Box>
  );
};

export default InspectionPlansPills;
